/**
 * 认证 API 服务
 * 处理用户登录、Token 刷新和用户信息获取
 */

import {
  saveToken,
  getToken,
  clearTokenStorage,
  setCurrentWalletAddress,
} from './request';
import {
  walletChallenge,
  walletVerify,
  refreshAuth,
  logout as apiLogout,
} from '@/api/auth';
import type { AuthTokenResponse, WalletChallenge } from '@/types/api';

/**
 * 登录请求参数（新钱包验签）
 */
export interface LoginRequest {
  wallet_address: string;
  signature: string;
  message: string;
  nonce: string;
  referrer_code?: string;
}

/**
 * 用户信息（legacy shape retained for callers）
 */
export interface UserInfo {
  id: number;
  wallet_address: string;
  user_level: number;
  power_level: number;
  referrer_id: number | null;
  referrer_code: string;
  power_value: string;
  today_output: string;
  total_output: string;
  created_at: string;
  updated_at: string;
}

/**
 * 登录响应（access_token aliases for legacy callers）
 */
export interface LoginResponse {
  access_token: string;
  accessToken: string;
  token_type: string;
  expires_in: number;
  user: UserInfo;
  userId?: string;
  address?: string;
  roles?: string[];
}

/**
 * Token 响应
 */
export interface TokenResponse {
  access_token: string;
  accessToken: string;
  token_type: string;
  expires_in: number;
}

function toTokenResponse(res: AuthTokenResponse): TokenResponse {
  return {
    access_token: res.accessToken,
    accessToken: res.accessToken,
    token_type: res.tokenType || 'Bearer',
    expires_in: res.expiresIn,
  };
}

/**
 * 认证服务类
 */
class AuthService {
  /**
   * 获取钱包登录 challenge（替代旧 nonce）
   */
  async getNonce(walletAddress: string): Promise<WalletChallenge> {
    return walletChallenge(walletAddress);
  }

  /**
   * 用户登录 — POST /v1/auth/wallet/verify
   */
  async login(loginRequest: LoginRequest): Promise<LoginResponse> {
    setCurrentWalletAddress(loginRequest.wallet_address);

    const response = await walletVerify({
      address: loginRequest.wallet_address,
      nonce: loginRequest.nonce,
      message: loginRequest.message,
      signature: loginRequest.signature,
    });

    saveToken(
      response.accessToken,
      response.expiresIn,
      loginRequest.wallet_address
    );

    const token = toTokenResponse(response);
    return {
      ...token,
      userId: response.userId,
      address: response.address,
      roles: response.roles,
      user: {
        id: 0,
        wallet_address: response.address || loginRequest.wallet_address,
        user_level: 0,
        power_level: 0,
        referrer_id: null,
        referrer_code: loginRequest.referrer_code || '',
        power_value: '0',
        today_output: '0',
        total_output: '0',
        created_at: '',
        updated_at: '',
      },
    };
  }

  /**
   * 刷新 Token — POST /v1/auth/refresh
   * 仅依赖 HttpOnly refresh Cookie（credentials include），无 body / 无 Bearer。
   */
  async refreshToken(): Promise<TokenResponse> {
    const response = await refreshAuth();
    return toTokenResponse(response);
  }

  /**
   * 获取当前用户信息 — 走组合接口，不再请求 /v1/auth/me
   */
  async getCurrentUser(): Promise<UserInfo> {
    const { getUserInfo } = await import('@/api/user');
    return getUserInfo();
  }

  /**
   * Clear local auth immediately, but do not report server logout until confirmed.
   */
  logout(): Promise<void> {
    const token = getToken();
    clearTokenStorage();
    setCurrentWalletAddress('');
    return apiLogout(token);
  }

  /**
   * 检查是否已登录
   */
  isAuthenticated(): boolean {
    return !!getToken();
  }

  /**
   * @deprecated 使用 getNonce() / walletChallenge 获取服务端 message
   */
  generateLoginMessage(): string {
    const timestamp = new Date().toISOString();
    return `Sign in to PANTA MARKET at ${timestamp}`;
  }
}

export const authService = new AuthService();
